/**
 * GMA-1618 (CompassCare Web 3/14 — Vitals triage) — pure triage model.
 *
 * The Vitals page ranks clients by **deviation from their personal baseline**,
 * not raw thresholds (a 20% jump matters more than an absolute number that is
 * normal for that person). This UI-free module holds that logic: per-vital
 * deviation, the human "±N% vs baseline" delta, a per-client triage score (the
 * worst deviation across their measured vitals), the triage sort, and staleness
 * (stale readings must never be shown as current).
 *
 * Fail-safe: missing/zero/non-finite values never throw and never divide by
 * zero — they resolve to null (not computable) and are skipped in ranking.
 */
import { toEpochMs } from "../../utils/webFormat";

// The vitals whose deviation feeds the triage score. BP is a pair, handled
// separately (systolic/diastolic each compared to its baseline).
export const TRIAGE_VITALS = Object.freeze(["hr", "spo2", "rr", "temp"]);

// A vital is "out of range" (highlighted + baseline delta surfaced) at or beyond
// this percent deviation from the personal baseline. Single source of truth —
// the triage table and the detail panel both consume it so the threshold can't
// drift between them.
export const OUT_OF_RANGE_PCT = 10;

/**
 * Signed percent deviation of `value` from `baseline`. Positive = above
 * baseline. Not computable (missing / non-finite / zero baseline) -> null.
 * @returns {number|null}
 */
export function deviationPct(value, baseline) {
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    typeof baseline !== "number" ||
    !Number.isFinite(baseline) ||
    // react-app#374: no vital sign of a living client is <= 0 — a zero is a
    // device/offline artifact, not a measurement. Treating it as absent both
    // suppresses the invented "-100% vs baseline" delta AND stops offline
    // rows from outranking genuinely critical clients in triage order.
    value <= 0 ||
    baseline <= 0
  ) {
    return null;
  }
  return ((value - baseline) / Math.abs(baseline)) * 100;
}

/**
 * Human baseline delta for an out-of-range cell, e.g. "+44% vs baseline" /
 * "-12% vs baseline". Not computable -> null (caller shows no delta).
 * @returns {string|null}
 */
export function formatBaselineDelta(value, baseline) {
  const pct = deviationPct(value, baseline);
  if (pct === null) return null;
  const rounded = Math.round(pct);
  const sign = rounded > 0 ? "+" : ""; // Math.round keeps the minus sign
  return `${sign}${rounded}% vs baseline`;
}

/**
 * Triage score for a client: the WORST (largest-magnitude) deviation, as an
 * absolute percent, across their measured triage vitals + BP. Higher = more
 * urgent. Missing vital/baseline pairs are skipped. No computable pair -> 0.
 * @param {object} vitals    e.g. { hr, spo2, rr, temp, bp: { systolic, diastolic } }
 * @param {object} baselines same shape (personal baselines)
 * @returns {number}
 */
export function triageScore(vitals, baselines) {
  const v = vitals || {};
  const b = baselines || {};
  let worst = 0;
  const consider = (value, baseline) => {
    const pct = deviationPct(value, baseline);
    if (pct !== null) worst = Math.max(worst, Math.abs(pct));
  };
  for (const key of TRIAGE_VITALS) consider(v[key], b[key]);
  // Blood pressure: compare each component to its baseline component.
  const vbp = v.bp || {};
  const bbp = b.bp || {};
  consider(vbp.systolic, bbp.systolic);
  consider(vbp.diastolic, bbp.diastolic);
  return worst;
}

/**
 * Order client rows by triage priority (worst deviation first). Non-mutating;
 * tolerates a non-array input and malformed rows. Rows carry `vitals` +
 * `baselines`; a `_triageScore` is not required (computed here).
 * @param {Array} rows
 * @returns {Array} new, sorted array (highest score first)
 */
export function sortByTriagePriority(rows) {
  // Fail-safe: drop rows we can't safely read fields from. `filter(Boolean)`
  // removed only falsy values, so a truthy primitive (`1`, `"bad"`, `true`) or
  // an array survived and rendered as a garbage "Unknown client" row. Keep
  // non-null, non-array objects — an empty `{}` is left in as a valid "unknown"
  // row (all fields render as their downstream fallbacks); the goal is only to
  // exclude primitives/arrays, not to enforce a strict plain-object shape.
  const list = Array.isArray(rows)
    ? rows.filter(
        (row) => row !== null && typeof row === "object" && !Array.isArray(row)
      )
    : [];
  return list
    .map((row) => ({ row, score: triageScore(row.vitals, row.baselines) }))
    .sort((a, b) => b.score - a.score)
    .map((x) => x.row);
}

/**
 * Whether a reading is stale (older than the threshold) — stale readings must
 * be flagged, never shown as current. A missing/unparseable timestamp is
 * treated as stale (we can't prove it is fresh).
 * @param {*} lastUpdate ISO string / epoch-ms / Date
 * @param {number} [now=Date.now()]
 * @param {number} [thresholdMinutes=15]
 * @returns {boolean}
 */
export function isStale(lastUpdate, now = Date.now(), thresholdMinutes = 15) {
  const t = toEpochMs(lastUpdate);
  if (t === null) return true;
  const ref = typeof now === "number" && Number.isFinite(now) ? now : Date.now();
  // A negative threshold would yield a negative window and mark nearly every
  // reading stale, so reject it along with non-finite values (fall back to 15).
  const minutes =
    typeof thresholdMinutes === "number" &&
    Number.isFinite(thresholdMinutes) &&
    thresholdMinutes >= 0
      ? thresholdMinutes
      : 15;
  const windowMs = minutes * 60000;
  return ref - t > windowMs;
}
